/**
 * Dev tools — a hidden panel (Ctrl+Shift+K) for simulating failure scenarios
 * on the Book page. Sets a localStorage flag so it persists across reloads
 * *during a testing session*.
 *
 * Safety: the failure-simulation flags (`failCoachMatch`, `failBooking`) reset
 * to false on every page load so a tester can't accidentally leave them on and
 * wonder why every booking fails. Only the `enabled` + `reportEmail` settings
 * persist; the simulation toggles are one-shot (set, test, auto-cleared on the
 * next load).
 *
 * These flags are read by the api.post calls in Book.tsx, which pass them as
 * `X-Simulate-Fail-*` headers. The BFF honours the headers (dev/test only).
 */

const STORAGE_KEY = 'itk-dev-tools';

export interface DevToolsState {
  enabled: boolean;
  failCoachMatch: boolean;
  failBooking: boolean;
  reportEmail: string;
}

const defaultState: DevToolsState = {
  enabled: false,
  failCoachMatch: false,
  failBooking: false,
  reportEmail: '',
};

/**
 * Load the dev tools state from localStorage. Only `reportEmail` persists; the
 * panel visibility (`enabled`) is NOT persisted (the panel never auto-opens on
 * reload — only ?dev=1 or Ctrl+Shift+K opens it), and the `failCoachMatch` +
 * `failBooking` simulation flags are always reset to false on load (one-shot —
 * a tester must re-enable them each session, so they can't be left on by
 * accident).
 */
export function loadDevTools(): DevToolsState {
  if (typeof window === 'undefined') return { ...defaultState };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultState };
    const persisted = JSON.parse(raw) as Partial<DevToolsState>;
    return {
      enabled: false, // never persisted — always starts closed
      failCoachMatch: false, // always reset on load
      failBooking: false, // always reset on load
      reportEmail: persisted.reportEmail ?? '',
    };
  } catch {
    return { ...defaultState };
  }
}

/** Save the dev tools state to localStorage. */
export function saveDevTools(state: DevToolsState): void {
  if (typeof window === 'undefined') return;
  // Only persist the report email. The panel visibility is NOT persisted —
  // the panel never auto-opens on reload (only ?dev=1 or Ctrl+Shift+K opens it),
  // so a tester can't accidentally leave it "stuck" visible across reloads.
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ reportEmail: state.reportEmail }),
  );
}

/**
 * Build the extra headers for a booking/preview request based on the dev tools
 * state. Returns headers the BFF honours to simulate failures + override the
 * report email. Only adds headers when the corresponding flag is set.
 */
export function devToolHeaders(state: DevToolsState): Record<string, string> {
  const headers: Record<string, string> = {};
  if (state.failCoachMatch) headers['X-Simulate-Fail'] = 'coach-match';
  if (state.failBooking) headers['X-Simulate-Fail'] = 'booking';
  if (state.reportEmail) headers['X-Report-Email'] = state.reportEmail;
  return headers;
}